
import { Vue, Options, prop } from "vue-class-component";
import { Commodity } from "@/domain/entities/Commodity";
import { CommodityData } from "@/domain/entities/BulkBooking";
import ModalAllCommodity from "../../../../booking/create-booking-v3/components/comodity/modal-all-commodity.vue";
import {
  DocInternationalModelValueType,
  CommodityCIPL,
  ErrorBookingV3
} from "@/domain/entities/CreateBookingV3";
import { parseZodError } from "../../../../booking/create-booking-v3/utils";
import {
  emailSchema,
  idNumberSchema,
  taxNumberSchema,
  itemDetailSchema,
  quantitySchema,
  goodsPriceSchema
} from "./validation";
import {
  formatInputMoney,
  formatInputMoneyWithDecimal,
  formatInputNumberOnly
} from "@/app/infrastructures/misc/Utils";
import { formatInputNoDecimal } from "@/app/infrastructures/misc/common-library/FormatInputNoDecimal";

interface CommoditySelectSearchRef {
  $refs: {
    selectSearch: HTMLElement;
  };
}

class Props {
  modelValue = prop<DocInternationalModelValueType>({
    required: true,
    default: () => new DocInternationalModelValueType()
  });
  error = prop<ErrorBookingV3>({
    type: Object,
    required: true
  });
  commodities = prop<CommodityData[]>({
    required: false,
    default: () => []
  });
  isLoadingCommodity = prop<boolean>({
    required: false,
    default: false
  });
  isEditSttMY = prop<boolean>({
    required: false,
    default: false
  });
  accountIsNonForeign = prop<boolean>({
    required: false,
    default: true
  });
  maxCipl = prop<number>({
    required: false,
    default: 15
  });
  indexListCommodityCIPL = prop<number>({
    required: false,
    default: 0
  });
  indexEmail = prop<number>({
    required: false,
    default: 0
  });
  indexTaxNumber = prop<number>({
    required: false,
    default: 0
  });
  indexIdNumber = prop<number>({
    required: false,
    default: 0
  });
  currentIndex = prop<number>({
    required: false,
    default: 0
  });
}

@Options({
  components: {
    ModalAllCommodity
  },
  watch: {
    currentIndex: {
      handler(index = 0) {
        this.updateFIeldIndex(index);
      }
    },
    "listCommodityCIPL.length": {
      handler(length: number) {
        if (!length) this.onAddCIPL(true);
      }
    }
  }
})
export default class SectionRepairDocInternational extends Vue.with(Props) {
  isModalViewAll = false;
  selectedCIPLIndex = 0;
  isDutyTaxTermsMenuOpen = false;
  isDgTypeMenuOpen = false;
  isQuarantineTypeMenuOpen = false;

  async mounted() {
    if (!this.listCommodityCIPL.length) {
      this.onAddCIPL(true);
    } else if (
      this.error.listCommodityCIPLDocInternational.length !==
      this.listCommodityCIPL.length
    ) {
      this.error.listCommodityCIPLDocInternational = [];
      this.listCommodityCIPL.forEach(() => {
        this.error.listCommodityCIPLDocInternational.push(["", "", ""]);
      });
    }
  }

  get listCommodityCIPL() {
    return this.modelValue.listCommodityCIPL || [];
  }

  set listCommodityCIPL(val: CommodityCIPL[]) {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      listCommodityCIPL: val
    });
  }

  formatCIPLGoodsPrice(value = 0) {
    return this.accountIsNonForeign
      ? formatInputMoney(value.toString().replace(/[\D]+/g, ""))
      : formatInputMoneyWithDecimal(value);
  }

  getTranslationText(translationKey = "") {
    return this.$t("bookingV3.docInternational." + translationKey);
  }

  normalizeDutyTaxTerms(value: { name: string; value: string } | string = "") {
    const dutyTaxTerms = typeof value === "string" ? value : value.value;
    return String(dutyTaxTerms)
      .trim()
      .toUpperCase() === "DDP"
      ? "DDP"
      : "DAP";
  }

  get effectiveDutyTaxTerms() {
    return this.normalizeDutyTaxTerms(this.modelValue.dutyTaxTerms);
  }

  get dutyTaxTermsOptions() {
    return [
      { name: this.getTranslationText("dutyTaxTermsOptionDDP"), value: "DDP" },
      { name: this.getTranslationText("dutyTaxTermsOptionDAP"), value: "DAP" }
    ];
  }

  get dgTypeOptions() {
    return [
      { name: this.getTranslationText("dgTypePlaceholder"), value: "" },
      { name: this.getTranslationText("dgTypeOptionNON"), value: "NON" },
      { name: this.getTranslationText("dgTypeOptionBAT"), value: "BAT" },
      { name: this.getTranslationText("dgTypeOptionLIQ"), value: "LIQ" },
      { name: this.getTranslationText("dgTypeOptionGAS"), value: "GAS" },
      { name: this.getTranslationText("dgTypeOptionMIX"), value: "MIX" }
    ];
  }

  get quarantineTypeOptions() {
    return [
      { name: this.getTranslationText("quarantineTypePlaceholder"), value: "" },
      { name: this.getTranslationText("quarantineTypeOptionNON"), value: "NON" },
      { name: this.getTranslationText("quarantineTypeOptionPLT"), value: "PLT" },
      { name: this.getTranslationText("quarantineTypeOptionANM"), value: "ANM" },
      { name: this.getTranslationText("quarantineTypeOptionFIS"), value: "FIS" },
      { name: this.getTranslationText("quarantineTypeOptionMIX"), value: "MIX" }
    ];
  }

  get dutyTaxTermsBannerText() {
    return this.modelValue.dutyTaxTerms === "DDP"
      ? this.getTranslationText("dutyTaxTermsBannerDDP")
      : this.getTranslationText("dutyTaxTermsBannerDAP");
  }

  onOpenDutyTaxTerms() {
    this.isDutyTaxTermsMenuOpen = true;
  }

  onCloseDutyTaxTerms() {
    this.isDutyTaxTermsMenuOpen = false;
  }

  onChooseDutyTaxTerms(_name: string, value: "DDP" | "DAP") {
    this.modelValue.dutyTaxTerms = value;
    this.error.dutyTaxTermsDocInternational = "";
    this.isDutyTaxTermsMenuOpen = false;
  }

  onOpenDgType() {
    this.isDgTypeMenuOpen = true;
  }

  onCloseDgType() {
    this.isDgTypeMenuOpen = false;
  }

  onChooseDgType(_name: string, value: string) {
    this.modelValue.dgType = value;
    this.isDgTypeMenuOpen = false;
  }

  onOpenQuarantineType() {
    this.isQuarantineTypeMenuOpen = true;
  }

  onCloseQuarantineType() {
    this.isQuarantineTypeMenuOpen = false;
  }

  onChooseQuarantineType(_name: string, value: string) {
    this.modelValue.quarantineType = value;
    this.isQuarantineTypeMenuOpen = false;
  }

  formatCountryCode(value = "") {
    return value.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 2);
  }

  handleInputCommodity(searchValue = "", fieldIndex = 0) {
    if (searchValue) {
      this.listCommodityCIPL[fieldIndex].commodity = new CommodityData({
        name: searchValue,
        code: searchValue
      });
      this.listCommodityCIPL[fieldIndex].name = searchValue;
      this.validateCIPLCommodity(fieldIndex);
    }

    this.$emit("filter:commodity", { search: searchValue, index: fieldIndex });
  }

  handleScrolledCommodity() {
    this.$emit("scroll:commodity");
  }

  changeCommodities(commodityValue: CommodityData, fieldIndex = 0) {
    this.listCommodityCIPL[fieldIndex].name = commodityValue.name || "";
  }

  onFocusCommodityList(parentIndexField = 0) {
    this.$emit("load:commodity", parentIndexField);
  }

  async onAddCIPL(isInitial = false) {
    if (this.listCommodityCIPL.length >= this.maxCipl) return;

    let lastIndex = 0;
    if (this.listCommodityCIPL.length) {
      lastIndex =
        this.listCommodityCIPL[this.listCommodityCIPL.length - 1]
          .indexGoodsPrice - this.indexListCommodityCIPL;
      lastIndex = Number(lastIndex.toFixed(3));
    }
    this.error.listCommodityCIPLDocInternational.push(["", "", ""]);
    this.listCommodityCIPL = [
      ...this.listCommodityCIPL,
      {
        id: new Date().getTime(),
        indexCommodity: this.indexListCommodityCIPL + (lastIndex + 0.01),
        indexQuantity: this.indexListCommodityCIPL + (lastIndex + 0.02),
        indexGoodsPrice: this.indexListCommodityCIPL + (lastIndex + 0.03),
        commodity: new CommodityData({
          name: "",
          code: ""
        }),
        name: "",
        quantity: "",
        goodsPrice: ""
      }
    ];

    if (isInitial) return;
    await this.$nextTick();
    const selectSearch = (this.$refs[
      `inputCommodity${this.listCommodityCIPL.length - 1}`
    ] as unknown) as CommoditySelectSearchRef;
    selectSearch.$refs.selectSearch.focus();
  }

  onDeleteCIPL(index = 0) {
    const updated = this.listCommodityCIPL.filter((_, i) => i !== index);
    this.listCommodityCIPL = updated;
    this.error.listCommodityCIPLDocInternational.splice(index, 1);
  }

  clickViewAll(index = 0) {
    this.selectedCIPLIndex = index;
    this.isModalViewAll = !this.isModalViewAll;
  }

  onChooseCommodity(val: Commodity) {
    const commodityName = this.accountIsNonForeign
      ? val.commodity_name
      : val.commodity_name_en || " ";
    this.listCommodityCIPL[
      this.selectedCIPLIndex
    ].commodity = new CommodityData({
      id: val.commodity_id,
      name: commodityName,
      code: val.commodity_code,
      status: val.commodity_status,
      isDangerousGoods: val.isDangerousGoods,
      isQuarantine: val.isQuarantine,
      hsCode: val.hs_code,
      nameId: val.commodity_name,
      nameEn: val.commodity_name_en
    });
    this.listCommodityCIPL[this.selectedCIPLIndex].name = commodityName;
    this.validateCIPLCommodity(this.selectedCIPLIndex);
    this.selectedCIPLIndex = 0;
  }

  decideBorderBottomTableColumn(index = 0) {
    return (this.listCommodityCIPL.length > 1 && index == 0) ||
      (index > 0 && index !== this.listCommodityCIPL.length - 1)
      ? "border-b border-gray-lp-2100"
      : "";
  }

  async updateFIeldIndex(index = 0) {
    if (index > this.currentIndex) {
      this.$emit("update:currentIndex", index);
    }

    await this.$nextTick();

    if (this.indexEmail < index) this.validateEmail();
    if (this.indexIdNumber < index) this.validateIdNumber();
    if (this.indexTaxNumber < index) this.validateTaxNumber();

    const ciplFieldIndex = this.listCommodityCIPL.findIndex(
      (cipl) =>
        cipl.indexCommodity === index ||
        cipl.indexGoodsPrice === index ||
        cipl.indexQuantity === index
    );

    for (let i = 0; i < ciplFieldIndex + 1; i++) {
      this.validateCIPLCommodity(i, index);
      this.validateCIPLQuantity(i, index);
      this.validateCIPLGoodsPrice(i, index);
    }

    this.handleTriggerErrorCIPL(index, ciplFieldIndex);
  }

  handleTriggerErrorCIPL(index = 0, ciplFieldIndex: number) {
    if (ciplFieldIndex !== -1) return;
    if (this.indexListCommodityCIPL < index) {
      for (let i = 0; i < this.listCommodityCIPL.length; i++) {
        this.validateCIPLCommodity(i);
        this.validateCIPLQuantity(i);
        this.validateCIPLGoodsPrice(i);
      }
    }
  }

  validateEmail() {
    const message = parseZodError(
      emailSchema(false, false, this.modelValue.email),
      this.modelValue.email
    );
    this.error.emailDocInternational = message;
  }

  validateIdNumber() {
    const message = parseZodError(
      idNumberSchema(false),
      this.modelValue.idNumber
    );
    this.error.idNumberDocInternational = message;
  }

  validateTaxNumber() {
    if (typeof this.modelValue.taxNumber === "string")
      this.modelValue.taxNumber = this.modelValue.taxNumber.slice(0, 99);
    const message = parseZodError(
      taxNumberSchema(false, false, this.modelValue.taxNumber, 100, 1),
      this.modelValue.taxNumber
    );
    this.error.taxNumberDocInternational = message;
  }

  async validateCIPLCommodity(index = 0, fieldIndex = -1) {
    if (this.$nextTick) await this.$nextTick();
    if (index >= this.modelValue.listCommodityCIPL.length) return;
    if (this.modelValue.listCommodityCIPL[index].indexCommodity === fieldIndex)
      return;
    if (!this.error.listCommodityCIPLDocInternational[index]) return;
    const commodityMessage = parseZodError(
      itemDetailSchema,
      this.modelValue.listCommodityCIPL[index].name || ""
    );
    this.error.listCommodityCIPLDocInternational[index][0] = commodityMessage;
  }

  validateCIPLQuantity(index = 0, fieldIndex = -1) {
    if (this.modelValue.listCommodityCIPL[index].indexQuantity === fieldIndex)
      return;
    if (!this.error.listCommodityCIPLDocInternational[index]) return;
    const quantityMessage = parseZodError(
      quantitySchema,
      this.modelValue.listCommodityCIPL[index].quantity
    );
    this.error.listCommodityCIPLDocInternational[index][1] = quantityMessage;
  }

  validateCIPLGoodsPrice(index = 0, fieldIndex = -1) {
    if (this.modelValue.listCommodityCIPL[index].indexGoodsPrice === fieldIndex)
      return;
    if (!this.error.listCommodityCIPLDocInternational[index]) return;
    const goodsPriceMessage = parseZodError(
      goodsPriceSchema,
      this.modelValue.listCommodityCIPL[index].goodsPrice
    );
    this.error.listCommodityCIPLDocInternational[index][2] = goodsPriceMessage;
  }

  decideShowError(errorMessage = "", fieldIndex = 0) {
    if (errorMessage && this.currentIndex === 0) return errorMessage;
    if (fieldIndex <= this.currentIndex) return errorMessage;
    return "";
  }

  formatCIPLQuantity(value: string) {
    const noDecimal = formatInputNoDecimal(String(value));
    return noDecimal;
  }

  formatCIPLQuantityNomorOnly(value: string) {
    const noDecimal = formatInputNumberOnly(String(value));
    return noDecimal;
  }
}
